/**
 * Erkennt, dass nach einem Deploy ein anderer Build ausgeliefert wird.
 *
 * Die SPA lädt sich nicht von selbst neu: Ein offener Tab läuft nach einem Deploy
 * mit dem alten Bundle weiter, und Tester prüften Fixes gegen den alten Stand
 * (T-0327). Der Build legt deshalb `version.json` neben `index.html`
 * (vite.config.ts), Nginx liefert sie ohne Cache aus, und der Tab vergleicht sie
 * mit dem, was in ihm steckt.
 */

export interface BuildKennung {
  version: string;
  commit: string;
}

const UNBEKANNT = 'unknown';

/** Was beim Build ins Bundle geschrieben wurde (dieselben Werte wie in der Sidebar). */
export function eingebauteKennung(): BuildKennung {
  return {
    version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0',
    commit: typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : UNBEKANNT,
  };
}

/** Liest den Inhalt von version.json; alles, was nicht danach aussieht, gilt als keine Antwort. */
export function leseKennung(daten: unknown): BuildKennung | null {
  if (!daten || typeof daten !== 'object' || Array.isArray(daten)) return null;
  const { version, commit } = daten as Record<string, unknown>;
  if (typeof version !== 'string' || !version.trim()) return null;
  return {
    version: version.trim(),
    commit: typeof commit === 'string' && commit.trim() ? commit.trim() : UNBEKANNT,
  };
}

/**
 * Läuft auf dem Server ein anderer Build als in diesem Tab?
 *
 * „Anders“, nicht „neuer“: Auch nach einem Rollback passt der Tab nicht mehr zum
 * Server. Der Pre-commit-Hook zählt die Version bei jedem Commit hoch; der Commit
 * fängt zusätzlich gleiche Nummern aus parallelen Branches ab. Kennt eine Seite
 * ihren Commit nicht (Build ohne .git und ohne GIT_COMMIT_SHA), zählt nur die Version.
 */
export function istAndereVersion(
  eingebaut: BuildKennung,
  ausgeliefert: BuildKennung | null,
): boolean {
  if (!ausgeliefert) return false;
  if (ausgeliefert.version !== eingebaut.version) return true;
  if (eingebaut.commit === UNBEKANNT || ausgeliefert.commit === UNBEKANNT) return false;
  return ausgeliefert.commit !== eingebaut.commit;
}

export const VERSIONS_URL = `${import.meta.env.BASE_URL}version.json`;

/**
 * Fragt ab, welcher Build gerade ausgeliefert wird. Scheitert die Abfrage — Deploy
 * läuft noch, offline, Datei fehlt im lokalen Dev-Server —, kommt `null` zurück:
 * Das ist kein Grund, irgendwem etwas anzuzeigen.
 */
export async function frageAusgelieferteKennung(
  abrufen: typeof fetch = fetch,
): Promise<BuildKennung | null> {
  try {
    const antwort = await abrufen(VERSIONS_URL, {
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!antwort.ok) return null;
    return leseKennung(await antwort.json());
  } catch {
    return null;
  }
}

/** Holt index.html neu und damit das Bundle, das der Server gerade ausliefert. */
export function ladeSeiteNeu(): void {
  window.location.reload();
}
